import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";

import MinimalLayout from "../components/layout/MinimalLayout";
import { Close } from "@mui/icons-material";
import { ButtonGradient } from "../components/theme/Button";
import { P1, P3 } from "../components/theme/text/Headings";
import {
  Black05,
  Black07,
  Black,
  Blue,
  Blue025,
  DarkGrey,
  NeonGreen,
  White,
  White08,
  White05,
  Purple,
  Purple05,
} from "../components/theme/colors";
import logo from "../images/logo/v.png";
import pro from "../images/ProTag.png";
import RevenueCatService from "../services/RevenueCatService";
import mixpanelService from "../services/MixpanelService";
import postHogService from "../services/PostHogService";
import { useAuth } from "../services/AuthContext";

const Container = styled.div`
  padding: 0 20px 30px;
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const AnimatedPage = styled.div`
  animation: ${(p) => (p.$isClosing ? slideDown : slideUp)} 400ms
    cubic-bezier(0.22, 0.61, 0.36, 1);
  will-change: transform, opacity;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const LogoCircle = styled.div`
  height: 75px;
  width: 75px;
  border-radius: 50%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${DarkGrey};
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
`;

const LogoContainer = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
`;

const ProTag = styled.img`
  position: absolute;
  bottom: -10px; /* slight overlap */
  right: -35px; /* slight overlap */
  width: 50px;
  z-index: 2; /* ensure on top of logo */
`;

const TopSection = styled.div`
  background: linear-gradient(120deg, ${Blue}, ${Purple});
  margin: 0 -20px 30px; /* bleed to screen edges to match container padding */
  padding: 80px 20px 16px; /* move top spacing here to avoid margin-collapsing */
  height: 200px;
  position: relative;
  overflow: hidden;
`;

const BottomWave = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px; /* hide any hairline gap */
  height: 90px;
  line-height: 0;
  pointer-events: none;
`;

const TopHeader = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1; /* keep header above the concave mask */
`;

const HeaderSpacer = styled.div`
  height: 36px;
  width: 36px;
`;

const CloseButton = styled.button`
  height: 36px;
  width: 36px;
  border-radius: 18px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Black07};
  color: ${White};
  cursor: pointer;
  margin-top: -35px;
`;

const Features = styled.div`
  margin: 35px 0px;
`;

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin: 25px 0;
`;

const FeatureIcon = styled.div`
  height: 45px;
  width: 45px;
  border-radius: 10px;
  background: ${DarkGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 24px;
  color: ${DarkGrey};
`;

const Plans = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 18px 0 24px;
`;

const PlanCard = styled.button`
  width: 100%;
  border-radius: 14px;
  border: 1.5px solid ${(p) => (p.$active ? Purple : Purple05)};
  background: ${DarkGrey};
  text-align: left;
  padding: 14px 16px;
  box-shadow: ${(p) => (p.$active ? `0 10px 24px ${Blue025}` : "none")};
`;

const Badge = styled.span`
  background: ${Purple};
  color: ${White};
  font-weight: 900;
  border-radius: 20px;
  padding: 6px 10px;
  font-size: 12px;
  margin-left: auto;
`;

const CurrentPlanBadge = styled.span`
  background: linear-gradient(120deg, ${Blue}, ${NeonGreen});
  color: ${White};
  font-weight: 900;
  border-radius: 20px;
  padding: 6px 10px;
  font-size: 12px;
  margin-left: auto;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 16px;
`;

const Link = styled.a`
  color: ${Black05};
  text-decoration: none;
  font-size: 14px;
`;

const plansData = [
  { id: "weekly", title: "Weekly", price: "$2.99", sub: "per week" },
  { id: "monthly", title: "Monthly", price: "$5.99", sub: "per month" },
  {
    id: "yearly",
    title: "Annual - 3 Days FREE Trial",
    price: "$49.99",
    sub: "per year",
    badge: "SAVE",
  },
];

export default function Pro() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUserState } = useAuth();
  const fromOnboarding = location.state?.fromOnboarding === true;
  const [selectedPlanId, setSelectedPlanId] = useState("yearly");

  // Pro status and current subscription
  const [isProUser, setIsProUser] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loadingProStatus, setLoadingProStatus] = useState(true);

  const [loadingAction, setLoadingAction] = useState(false);

  // Offerings state (prevents Apple race condition)
  const [offerings, setOfferings] = useState(null);
  const [offeringsLoading, setOfferingsLoading] = useState(true);

  // Map both RevenueCat package IDs and StoreKit product IDs to our plan IDs
  const getProPlanFromProductId = (productId) => {
    const productToPlan = {
      // RevenueCat package identifiers
      $rc_weekly: "weekly",
      $rc_monthly: "monthly",
      $rc_annual: "yearly",
      // StoreKit product identifiers
      "com.vibecheckme.ios.Weekly": "weekly",
      "com.vibecheckme.ios.Monthly": "monthly",
      "com.vibecheckme.ios.Annual": "yearly",
    };
    return productToPlan[productId] || null;
  };

  useEffect(() => {
    const loadOfferings = async () => {
      try {
        const result = await RevenueCatService.getOfferings();
        setOfferings(result);
      } catch (err) {
        console.warn("⚠️ Failed to load offerings:", err);
      } finally {
        setOfferingsLoading(false);
      }
    };

    loadOfferings();
  }, []);

  // Check Pro status and current subscription on mount
  useEffect(() => {
    const checkProStatus = async () => {
      try {
        const customerInfo = await RevenueCatService.getCustomerInfo();
        const isPro = RevenueCatService.isProFromCustomerInfo(customerInfo);
        setIsProUser(isPro);

        if (isPro && customerInfo?.customerInfo) {
          console.log("🔍 Current subscription detection debug:", {
            activeSubscriptions:
              customerInfo.customerInfo.activeSubscriptions || [],
            allPurchasedProductIdentifiers:
              customerInfo.customerInfo.allPurchasedProductIdentifiers || [],
            entitlements: customerInfo.customerInfo.entitlements?.active || {},
            allPurchaseDates: customerInfo.customerInfo.allPurchaseDates || {},
            nonSubscriptionTransactions:
              customerInfo.customerInfo.nonSubscriptionTransactions || [],
            originalPurchaseDate:
              customerInfo.customerInfo.originalPurchaseDate,
            requestDate: customerInfo.customerInfo.requestDate,
          });

          // Try multiple approaches to find the current subscription
          let currentProduct = null;
          let currentPlanId = null;

          // Approach 1: Check activeSubscriptions with purchase dates
          const activeSubscriptions =
            customerInfo.customerInfo.activeSubscriptions || [];
          const allPurchaseDates =
            customerInfo.customerInfo.allPurchaseDates || {};

          if (activeSubscriptions.length > 0) {
            let mostRecentDate = null;
            for (const productId of activeSubscriptions) {
              const purchaseDate = allPurchaseDates[productId];
              if (
                purchaseDate &&
                (!mostRecentDate ||
                  new Date(purchaseDate) > new Date(mostRecentDate))
              ) {
                currentProduct = productId;
                mostRecentDate = purchaseDate;
              }
            }
          }

          // Approach 2: Check all purchased products if no active subscription found
          if (!currentProduct) {
            const allPurchasedProducts =
              customerInfo.customerInfo.allPurchasedProductIdentifiers || [];
            let mostRecentDate = null;
            for (const productId of allPurchasedProducts) {
              const purchaseDate = allPurchaseDates[productId];
              if (
                purchaseDate &&
                (!mostRecentDate ||
                  new Date(purchaseDate) > new Date(mostRecentDate))
              ) {
                currentProduct = productId;
                mostRecentDate = purchaseDate;
              }
            }
          }

          // Approach 3: Fallback to first available
          if (!currentProduct) {
            if (activeSubscriptions.length > 0) {
              currentProduct = activeSubscriptions[0];
            } else {
              const allPurchasedProducts =
                customerInfo.customerInfo.allPurchasedProductIdentifiers || [];
              if (allPurchasedProducts.length > 0) {
                currentProduct = allPurchasedProducts[0];
              }
            }
          }

          if (currentProduct) {
            currentPlanId = getProPlanFromProductId(currentProduct);
          }

          if (currentPlanId) {
            setCurrentSubscription(currentPlanId);
            setSelectedPlanId(currentPlanId);
          }
        }
      } catch (error) {
      } finally {
        setLoadingProStatus(false);
      }
    };

    checkProStatus();
  }, []);

  // Track Pro page view - only once on mount
  useEffect(() => {
    const proPageViewProps = {
      user_id: currentUserState?.uid || "anonymous",
      is_pro_user: isProUser,
      current_subscription: currentSubscription,
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Pro Page Viewed", proPageViewProps);
    postHogService.track("Pro Page Viewed", proPageViewProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track plan selection
  const handlePlanSelect = (planId) => {
    setSelectedPlanId(planId);

    const planSelectProps = {
      plan_type: planId,
      user_id: currentUserState?.uid || "anonymous",
      is_pro_user: isProUser,
      current_subscription: currentSubscription,
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Subscription Plan Selected", planSelectProps);
    postHogService.track("Subscription Plan Selected", planSelectProps);
  };

  const handleSubscribe = async () => {
    if (offeringsLoading) {
      alert("Offerings are still loading. Please wait a moment.");
      return;
    }

    if (!offerings?.current) {
      alert("Offerings not available yet. Please try again shortly.");
      return;
    }

    setLoadingAction(true);
    try {
      const currentCustomerInfo = await RevenueCatService.getCustomerInfo();
      const hasActiveSubscription =
        currentCustomerInfo?.customerInfo?.activeSubscriptions?.length > 0;
      const currentActiveSubscription =
        currentCustomerInfo?.customerInfo?.activeSubscriptions?.[0];

      const current = offerings.current;

      const PLAN_TO_PACKAGE_IDENTIFIER = {
        weekly: "$rc_weekly",
        monthly: "$rc_monthly",
        yearly: "$rc_annual",
      };

      const wantedIdentifier = PLAN_TO_PACKAGE_IDENTIFIER[selectedPlanId];

      let match = (current.availablePackages || []).find(
        (p) => p?.identifier === wantedIdentifier
      );

      if (!match) {
        const convenience = current?.[selectedPlanId];
        match = Array.isArray(convenience) ? convenience[0] : convenience;
      }

      if (!match) {
        alert(
          `No matching package found for ${selectedPlanId}. Check package identifiers in RevenueCat.`
        );
        return;
      }

      const targetProductId = `com.vibecheckme.ios.${
        selectedPlanId.charAt(0).toUpperCase() + selectedPlanId.slice(1)
      }`;

      const isSubscriptionChange =
        hasActiveSubscription && currentActiveSubscription !== targetProductId;

      const result = await RevenueCatService.purchasePackage(match);

      const info = result?.customerInfo || result;
      const pro = RevenueCatService.isProFromCustomerInfo(info);

      if (pro) {
        const purchasedProductId =
          info?.customerInfo?.entitlements?.active?.[
            "Vibe Check: Compatibility Quiz Pro"
          ]?.productIdentifier ||
          info?.customerInfo?.entitlements?.active?.[
            "vibe check: compatibility quiz pro"
          ]?.productIdentifier ||
          info?.customerInfo?.entitlements?.all?.[
            "Vibe Check: Compatibility Quiz Pro"
          ]?.productIdentifier ||
          info?.customerInfo?.entitlements?.all?.[
            "vibe check: compatibility quiz pro"
          ]?.productIdentifier ||
          info?.customerInfo?.entitlements?.active?.Pro?.productIdentifier ||
          info?.customerInfo?.entitlements?.active?.pro?.productIdentifier ||
          info?.customerInfo?.entitlements?.all?.Pro?.productIdentifier ||
          info?.customerInfo?.entitlements?.all?.pro?.productIdentifier;

        const isCorrectPurchase = purchasedProductId === targetProductId;

        if (isCorrectPurchase) {
          alert(
            `${
              selectedPlanId.charAt(0).toUpperCase() + selectedPlanId.slice(1)
            } subscription activated successfully – Pro unlocked!`
          );
        } else if (isSubscriptionChange && !isCorrectPurchase) {
          alert(
            `Subscription changed to the ${selectedPlanId} plan! The change will take effect at your next renewal date.`
          );
        } else {
          alert(`Purchase successful – Pro unlocked!`);
        }

        // Track subscription success
        const subscriptionProps = {
          plan_type: selectedPlanId,
          user_id: currentUserState?.uid || "anonymous",
          is_subscription_change: isSubscriptionChange,
          previous_subscription: currentSubscription,
          product_id: targetProductId,
          timestamp: new Date().toISOString(),
        };
        mixpanelService.track("Subscription Started", subscriptionProps);
        postHogService.track("Subscription Started", subscriptionProps);

        // Refresh customer info after delay to reflect new subscription
        await new Promise((resolve) => setTimeout(resolve, 4000));
        const updated = await RevenueCatService.getCustomerInfo();
        const isPro = RevenueCatService.isProFromCustomerInfo(updated);
        setIsProUser(isPro);

        // Update current subscription to reflect the change
        if (isPro) {
          const activeSubscriptions =
            updated?.customerInfo?.activeSubscriptions || [];
          if (activeSubscriptions.length > 0) {
            const currentProduct = activeSubscriptions[0];
            const currentPlanId = getProPlanFromProductId(currentProduct);
            if (currentPlanId) {
              setCurrentSubscription(currentPlanId);
            }
          }
        }
      }
    } catch (e) {
      const message = e?.userCancelled
        ? "Purchase cancelled"
        : e?.message || "Purchase failed";
      alert(message);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleRestore = async () => {
    setLoadingAction(true);
    try {
      await RevenueCatService.restorePurchases();
      alert("Restore complete");
    } catch (e) {
      alert(e?.message || "Restore failed");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCancelSubscription = async () => {
    // Show confirmation before redirecting
    const confirmed = window.confirm(
      "You'll be redirected to Apple's subscription management page where you can cancel your subscription. Continue?"
    );

    if (!confirmed) return;

    try {
      // For iOS, get the management URL from RevenueCat
      const customerInfo = await RevenueCatService.getCustomerInfo();
      const managementURL = customerInfo?.customerInfo?.managementURL;

      if (managementURL) {
        // Open Apple's subscription management page
        window.open(managementURL, "_blank", "noopener,noreferrer");
      } else {
        // Fallback to Apple's general subscription management
        window.open(
          "https://apps.apple.com/account/subscriptions",
          "_blank",
          "noopener,noreferrer"
        );
      }
    } catch (error) {
      // Fallback to Apple's general subscription management
      window.open(
        "https://apps.apple.com/account/subscriptions",
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const [isClosing, setIsClosing] = useState(false);
  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      if (fromOnboarding) {
        navigate("/home", { replace: true });
      } else {
        navigate(-1);
      }
    }, 200);
  };

  return (
    <MinimalLayout hideFooter>
      <AnimatedPage $isClosing={isClosing}>
        <Container>
          <TopSection>
            <TopHeader>
              <HeaderSpacer />
              <LogoContainer style={{ margin: 0 }}>
                <LogoCircle>
                  <img
                    src={logo}
                    alt="Vibe Check Pro"
                    style={{ width: "80%" }}
                  />
                </LogoCircle>
                <ProTag src={pro} alt="Pro tag" />
              </LogoContainer>
              <CloseButton onClick={handleClose} aria-label="Close">
                <Close />
              </CloseButton>
            </TopHeader>
            <BottomWave>
              <svg
                viewBox="0 0 1440 90"
                preserveAspectRatio="none"
                style={{ display: "block", width: "100%", height: "100%" }}
              >
                <path
                  d="M0,60 C 240,90 1200,90 1440,60 L1440,90 L0,90 Z"
                  fill={Black}
                />
              </svg>
            </BottomWave>
          </TopSection>

          <Features>
            <Feature>
              <FeatureIcon>⚡</FeatureIcon>
              <div>
                <P1 color={White} style={{ fontWeight: 900 }}>
                  AI Insights
                </P1>
                <P3 color={White08}>Find in depth insights</P3>
              </div>
            </Feature>
            <Feature>
              <FeatureIcon>👀</FeatureIcon>
              <div>
                <P1 color={White} style={{ fontWeight: 900 }}>
                  Their Answers
                </P1>
                <P3 color={White08}>Look how they answered each question</P3>
              </div>
            </Feature>
            <Feature>
              <FeatureIcon>🔒</FeatureIcon>
              <div>
                <P1 color={White} style={{ fontWeight: 900 }}>
                  Unlock New Fields
                </P1>
                <P3 color={White08}>See more fields in each vibe check</P3>
              </div>
            </Feature>
          </Features>

          <Plans>
            {plansData.map((plan) => (
              <PlanCard
                key={plan.id}
                onClick={() => handlePlanSelect(plan.id)}
                $active={selectedPlanId === plan.id}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 900, color: White08 }}>
                      {plan.title}
                    </div>
                    <div style={{ color: White05 }}>
                      <span style={{ fontWeight: 900 }}>{plan.price}</span>
                      {plan.sub ? (
                        <span style={{ marginLeft: 6 }}>({plan.sub})</span>
                      ) : null}
                    </div>
                  </div>
                  {currentSubscription === plan.id ? (
                    <CurrentPlanBadge>CURRENT</CurrentPlanBadge>
                  ) : plan.badge ? (
                    <Badge>{plan.badge}</Badge>
                  ) : null}
                </div>
              </PlanCard>
            ))}
          </Plans>

          <ButtonGradient
            className="mt-15"
            width="100%"
            $background={Blue}
            $transitionBackground={Purple}
            onClick={handleSubscribe}
            disabled={
              offeringsLoading ||
              !offerings?.current ||
              loadingAction ||
              loadingProStatus ||
              (isProUser && currentSubscription === selectedPlanId)
            }
          >
            {offeringsLoading
              ? "Loading..."
              : loadingAction
              ? "Processing..."
              : isProUser && currentSubscription === selectedPlanId
              ? "Current Plan"
              : isProUser
              ? "Change Plan"
              : "Continue to Pro"}
          </ButtonGradient>

          <div className="mt-20 mb-30" style={{ textAlign: "center" }}>
            {isProUser && currentSubscription ? (
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleCancelSubscription();
                }}
                style={{
                  color: White05,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Cancel Subscription
              </Link>
            ) : (
              <div
                style={{
                  color: White05,
                }}
              >
                Cancel Anytime
              </div>
            )}
          </div>

          <FooterLinks>
            <Link
              href="https://www.vibecheckme.com/privacy-policy"
              target="_blank"
              rel="noreferrer"
              style={{
                color: White05,
              }}
            >
              Privacy policy
            </Link>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleRestore();
              }}
              style={{
                color: White05,
              }}
            >
              Restore
            </Link>
            <Link
              href="https://www.vibecheckme.com/terms-of-service"
              target="_blank"
              rel="noreferrer"
              style={{
                color: White05,
              }}
            >
              Terms of service
            </Link>
          </FooterLinks>
        </Container>
      </AnimatedPage>
    </MinimalLayout>
  );
}
